import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { typedRpcValue } from '@/lib/supabaseTypedQuery';

/**
 * Two-step verification (TOTP) through Supabase Auth MFA.
 *
 * A verified factor raises the session to aal2 when a code is entered
 * (challenge + verify). Workspaces that require two-step verification check
 * the session's aal claim server-side (app_workspace_mfa_satisfied).
 */

export interface TotpFactor {
  id: string;
  friendlyName: string | null;
  status: 'verified' | 'unverified';
  createdAt: string;
}

export interface TotpEnrolment {
  factorId: string;
  qrCode: string;
  secret: string;
}

export interface WorkspaceMfaStatus {
  required: boolean;
  satisfied: boolean;
  session_aal: string;
  sso_session: boolean;
}

export const mfaFactorsKey = (userId: string | null | undefined) => ['auth-mfa-factors', userId ?? null] as const;
export const workspaceMfaStatusKey = (workspaceId: string | null | undefined) => ['workspace-mfa-status', workspaceId ?? null] as const;

function toFactor(raw: { id: string; friendly_name?: string | null; status: string; created_at: string }): TotpFactor {
  return {
    id: raw.id,
    friendlyName: raw.friendly_name ?? null,
    status: raw.status === 'verified' ? 'verified' : 'unverified',
    createdAt: raw.created_at,
  };
}

async function listTotpFactors(): Promise<TotpFactor[]> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw error;
  return (data?.all ?? [])
    .filter(factor => factor.factor_type === 'totp')
    .map(toFactor);
}

export function useTotpFactors(userId: string | null | undefined) {
  return useQuery({
    queryKey: mfaFactorsKey(userId),
    enabled: !!userId,
    queryFn: listTotpFactors,
  });
}

/** The server's view of this session against one workspace's requirement. */
export function useWorkspaceMfaStatus(workspaceId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: workspaceMfaStatusKey(workspaceId),
    enabled: !!workspaceId && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<WorkspaceMfaStatus> => {
      const { data, error } = await typedRpcValue<WorkspaceMfaStatus>('app_get_workspace_mfa_status', {
        p_workspace_id: workspaceId,
      });
      if (error) throw error;
      if (!data) throw new Error('Two-step verification status is unavailable');
      return data;
    },
  });
}

export const sessionAalKey = (userId: string | null | undefined) => ['auth-session-aal', userId ?? null] as const;

/** 'aal2' once this session has passed a code; read from the local session. */
export function useSessionAal(userId: string | null | undefined) {
  return useQuery({
    queryKey: sessionAalKey(userId),
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error) throw error;
      return { current: data.currentLevel ?? 'aal1', next: data.nextLevel ?? 'aal1' };
    },
  });
}

function useInvalidateMfa() {
  const queryClient = useQueryClient();
  return () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['auth-mfa-factors'] }),
    queryClient.invalidateQueries({ queryKey: ['auth-session-aal'] }),
    queryClient.invalidateQueries({ queryKey: ['workspace-mfa-status'] }),
    // Audit rows are hidden until a required second step is passed.
    queryClient.invalidateQueries({ queryKey: ['workspace-audit-log'] }),
  ]);
}

/** Starts a fresh authenticator enrolment, clearing any abandoned attempt first. */
export function useStartTotpEnrolment() {
  return useMutation({
    mutationFn: async (): Promise<TotpEnrolment> => {
      const existing = await listTotpFactors();
      for (const factor of existing.filter(f => f.status === 'unverified')) {
        const { error } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
        if (error) throw error;
      }
      const verifiedCount = existing.filter(f => f.status === 'verified').length;
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `Authenticator ${verifiedCount + 1} (${new Date().toISOString().slice(0, 10)})`,
      });
      if (error) throw error;
      return { factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret };
    },
  });
}

/** Verifies a 6-digit code; on success the session is aal2. */
export function useVerifyTotp() {
  const invalidate = useInvalidateMfa();
  return useMutation({
    mutationFn: async ({ factorId, code }: { factorId: string; code: string }) => {
      const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: code.replace(/\s+/g, '') });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function useRemoveTotp() {
  const invalidate = useInvalidateMfa();
  return useMutation({
    mutationFn: async (factorId: string) => {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function isSixDigitCode(value: string): boolean {
  return /^\d{6}$/.test(value.replace(/\s+/g, ''));
}
